import React, { useState, useEffect, useRef } from 'react';
import { Upload, Search, Trash2, FolderOpen, Folder, FolderPlus, Cloud, HardDrive, X, Link2, MoreVertical, Pencil, Copy, LayoutGrid, List, ChevronRight, Home, CheckCircle2, Check } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import DropZone from '../../components/DropZone';
import { requestPin } from '../../components/PinModal';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function AdminMedia() {
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState('');
  const [view, setView] = useState(() => localStorage.getItem('mediaView') || 'grid');
  const [q, setQ] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [franchises, setFranchises] = useState([]);
  const [detail, setDetail] = useState(null);
  const [usage, setUsage] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [deletingFolder, setDeletingFolder] = useState(null);
  const [folderForm, setFolderForm] = useState(null); // {mode:'create'|'rename', target?, name}
  const [dragOver, setDragOver] = useState(null);
  const [selected, setSelected] = useState([]);
  const inputRef = useRef(null);

  const load = () => api.getMedia({ q, owner: ownerFilter, folder: q ? '' : (currentFolder || '/') }).then(setFiles).catch(() => {});
  const loadFolders = () => api.getMediaFolders().then(setFolders).catch(() => {});

  useEffect(() => {
    load();
    setSelected([]);
    // eslint-disable-next-line
  }, [q, ownerFilter, currentFolder]);

  useEffect(() => {
    loadFolders();
    api.getMediaStatus().then(setStatus).catch(() => {});
    api.getFranchisesFresh().then(setFranchises).catch(() => {});
  }, []);

  const setViewMode = (v) => { setView(v); localStorage.setItem('mediaView', v); };

  const subFolders = folders.filter((f) => {
    if (!currentFolder) return !f.path.includes('/');
    return f.path.startsWith(`${currentFolder}/`) && !f.path.slice(currentFolder.length + 1).includes('/');
  });
  const crumbs = currentFolder ? currentFolder.split('/') : [];

  const handleFiles = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      for (const file of fileList) {
        await api.uploadMedia(file, ownerFilter || 'matriz', currentFolder);
        toast.success(`${file.name} subido${currentFolder ? ` a ${currentFolder}` : ''}`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const saveFolder = async () => {
    const name = folderForm.name.trim();
    if (!name) return;
    try {
      if (folderForm.mode === 'create') {
        await api.createMediaFolder(name, currentFolder);
        toast.success(`Carpeta "${name}" creada`);
      } else {
        await api.renameMediaFolder(folderForm.target.id, name);
        toast.success('Carpeta renombrada');
      }
      setFolderForm(null);
      loadFolders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar la carpeta');
    }
  };

  const duplicateFolder = async (f) => {
    try {
      const copy = await api.duplicateMediaFolder(f.id);
      toast.success(`Carpeta duplicada: ${copy.path.split('/').pop()}`);
      loadFolders(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al duplicar');
    }
  };

  const confirmDeleteFolder = async () => {
    try {
      await api.deleteMediaFolder(deletingFolder.id);
      toast.success('Carpeta eliminada. Sus archivos se movieron a "Sin carpeta".');
      setDeletingFolder(null);
      loadFolders(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const moveFile = async (f, folder) => {
    await moveFiles([f.id], folder);
  };

  const moveFolder = async (id, parent) => {
    try {
      await api.moveMediaFolder(id, parent);
      toast.success(parent ? `Carpeta movida dentro de ${parent}` : 'Carpeta movida a la raíz');
      loadFolders(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al mover la carpeta');
    }
  };

  const moveFiles = async (ids, folder) => {
    try {
      let last = null;
      for (const id of ids) last = await api.updateMedia(id, { folder });
      toast.success(ids.length > 1
        ? `${ids.length} archivos movidos${folder ? ` a ${folder}` : ' a la raíz'}`
        : (folder ? `Movido a ${folder}` : 'Movido a la raíz'));
      setSelected([]);
      if (detail && ids.includes(detail.id)) setDetail(last);
      load();
    } catch { toast.error('Error al mover'); }
  };

  const onFileClick = (f) => {
    setSelected((prev) => prev.includes(f.id) ? prev.filter((id) => id !== f.id) : [...prev, f.id]);
    openDetail(f);
  };

  const dropProps = (folderPath) => ({
    onDragOver: (e) => { if (e.dataTransfer.types.includes('fileids') || e.dataTransfer.types.includes('folderid')) { e.preventDefault(); e.stopPropagation(); setDragOver(folderPath); } },
    onDragLeave: () => setDragOver(null),
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation();
      setDragOver(null);
      const raw = e.dataTransfer.getData('fileids');
      if (raw) {
        const ids = JSON.parse(raw).filter((id) => files.find((x) => x.id === id)?.folder !== folderPath);
        if (ids.length) moveFiles(ids, folderPath);
        return;
      }
      const folderId = e.dataTransfer.getData('folderid');
      if (folderId) {
        const src = folders.find((x) => x.id === folderId);
        if (src && src.path !== folderPath) moveFolder(folderId, folderPath);
      }
    },
  });

  const dragProps = (f) => ({
    draggable: true,
    onDragStart: (e) => {
      const ids = selected.includes(f.id) ? selected : [f.id];
      e.dataTransfer.setData('fileids', JSON.stringify(ids));
    },
  });

  const folderDragProps = (f) => ({
    draggable: true,
    onDragStart: (e) => e.dataTransfer.setData('folderid', f.id),
  });

  const openDetail = async (f) => {
    setDetail(f);
    setUsage([]);
    try { setUsage(await api.getMediaUsage(f.id)); } catch { /* noop */ }
  };

  const toggleActive = async (f) => {
    try {
      const updated = await api.updateMedia(f.id, { active: !f.active });
      setFiles((prev) => prev.map((x) => x.id === f.id ? updated : x));
      if (detail?.id === f.id) setDetail(updated);
    } catch { toast.error('Error al actualizar'); }
  };

  const confirmDelete = async () => {
    const token = await requestPin(`Eliminar archivo "${deleting.fileName}"`);
    if (!token) return;
    try {
      await api.deleteMedia(deleting.id, token);
      setFiles((prev) => prev.filter((f) => f.id !== deleting.id));
      setDeleting(null); setDetail(null);
      toast.success('Archivo eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const FolderMenu = ({ f }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} className="p-1 rounded hover:bg-stone-200 text-stone-400 hover:text-stone-700 transition-colors" data-testid={`folder-menu-${f.id}`}>
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => setFolderForm({ mode: 'rename', target: f, name: f.path.split('/').pop() })} data-testid={`folder-rename-${f.id}`}>
          <Pencil className="w-3.5 h-3.5 mr-2" /> Renombrar
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => duplicateFolder(f)} data-testid={`folder-duplicate-${f.id}`}>
          <Copy className="w-3.5 h-3.5 mr-2" /> Duplicar (con contenido)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setDeletingFolder(f)} className="text-red-600 focus:text-red-600" data-testid={`folder-delete-${f.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-2" /> Eliminar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div data-testid="admin-media">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <FolderOpen className="w-6 h-6 text-amber-500" />
            <h1 className="text-2xl font-black text-stone-800">Archivos Multimedia</h1>
          </div>
          <p className="text-sm text-stone-500">Biblioteca corporativa única. Todos los módulos utilizan estos archivos.</p>
        </div>
        <label className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm cursor-pointer">
          <Upload className="w-4 h-4" /> {uploading ? 'Subiendo…' : 'Subir archivo'}
          <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => handleFiles(Array.from(e.target.files || []))} disabled={uploading} data-testid="media-upload-input" />
        </label>
      </div>

      {status && (
        <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 mb-6 text-sm ${status.graphConfigured ? 'bg-teal-50 border-teal-200 text-teal-800' : 'bg-stone-50 border-stone-200 text-stone-600'}`} data-testid="media-status">
          {status.graphConfigured ? <Cloud className="w-5 h-5 shrink-0" /> : <HardDrive className="w-5 h-5 shrink-0" />}
          {status.graphConfigured
            ? <span><strong>Microsoft Graph activo</strong> · Repositorio: OneDrive └ {status.folder}</span>
            : <span><strong>Almacenamiento local activo.</strong> Conector Microsoft Graph preparado: al añadir AZURE_TENANT_ID / AZURE_CLIENT_ID / AZURE_CLIENT_SECRET / ONEDRIVE_USER en .env, las subidas irán a OneDrive └ {status.folder} sin tocar código.</span>}
        </div>
      )}

      {/* Migas de pan + acciones de carpeta + vista */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <nav className="flex items-center gap-1 text-sm flex-1 min-w-48" data-testid="media-breadcrumb">
          <button onClick={() => setCurrentFolder('')} {...dropProps('')} className={`flex items-center gap-1.5 px-2 py-1 rounded font-bold transition-colors ${dragOver === '' ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-400' : !currentFolder ? 'text-amber-600' : 'text-stone-500 hover:text-stone-800 hover:bg-stone-100'}`} data-testid="breadcrumb-root">
            <Home className="w-3.5 h-3.5" /> Biblioteca
          </button>
          {crumbs.map((c, i) => {
            const path = crumbs.slice(0, i + 1).join('/');
            return (
              <span key={path} className="flex items-center gap-1">
                <ChevronRight className="w-3.5 h-3.5 text-stone-300" />
                <button onClick={() => setCurrentFolder(path)} {...dropProps(path)} className={`px-2 py-1 rounded font-bold transition-colors ${dragOver === path ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-400' : path === currentFolder ? 'text-amber-600' : 'text-stone-500 hover:text-stone-800 hover:bg-stone-100'}`}>{c}</button>
              </span>
            );
          })}
        </nav>
        <button onClick={() => setFolderForm({ mode: 'create', name: '' })} className="flex items-center gap-1.5 border border-stone-300 bg-white hover:border-amber-400 hover:text-amber-600 transition-colors rounded px-3 py-1.5 text-sm font-bold text-stone-600" data-testid="new-folder-button">
          <FolderPlus className="w-4 h-4" /> Nueva carpeta
        </button>
        <div className="flex border border-stone-300 rounded overflow-hidden bg-white">
          <button onClick={() => setViewMode('grid')} className={`p-2 transition-colors ${view === 'grid' ? 'bg-amber-400 text-white' : 'text-stone-400 hover:text-stone-700'}`} title="Vista iconos" data-testid="view-grid-button"><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => setViewMode('list')} className={`p-2 transition-colors ${view === 'list' ? 'bg-amber-400 text-white' : 'text-stone-400 hover:text-stone-700'}`} title="Vista lista" data-testid="view-list-button"><List className="w-4 h-4" /></button>
        </div>
      </div>

      {folderForm && (
        <div className="bg-white rounded-lg border-2 border-amber-300 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="folder-form">
          <Folder className="w-5 h-5 text-amber-500" />
          <span className="text-sm font-bold text-stone-700">
            {folderForm.mode === 'create' ? `Nueva carpeta ${currentFolder ? `dentro de "${currentFolder}"` : 'en la raíz'}` : `Renombrar "${folderForm.target.path.split('/').pop()}"`}
          </span>
          <input
            autoFocus
            value={folderForm.name}
            onChange={(e) => setFolderForm({ ...folderForm, name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && saveFolder()}
            placeholder="Nombre de la carpeta"
            className="border border-stone-300 rounded px-3 py-2 text-sm flex-1 min-w-48 focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="folder-name-input"
          />
          <button onClick={saveFolder} className="btn-natur px-4 py-2 rounded text-sm font-bold" data-testid="folder-save-button">Guardar</button>
          <button onClick={() => setFolderForm(null)} className="px-3 py-2 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
        </div>
      )}

      {deletingFolder && (
        <div className="bg-white rounded-lg border-2 border-red-300 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="folder-delete-confirm">
          <Trash2 className="w-5 h-5 text-red-500" />
          <span className="text-sm text-stone-700 flex-1 min-w-48">Eliminar la carpeta <strong>“{deletingFolder.path}”</strong> (y sus subcarpetas). Los archivos <strong>no se borran</strong>: pasan a “Sin carpeta”.</span>
          <button onClick={confirmDeleteFolder} className="bg-red-600 hover:bg-red-700 transition-colors text-white px-4 py-2 rounded text-sm font-bold" data-testid="folder-delete-confirm-button">Eliminar carpeta</button>
          <button onClick={() => setDeletingFolder(null)} className="px-3 py-2 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-64">
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en toda la biblioteca…" className="w-full border border-stone-300 rounded pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="media-search" />
        </div>
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className="border border-stone-300 rounded px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="media-owner-filter">
          <option value="">Todas las franquicias (MATRIZ)</option>
          {franchises.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
      </div>

      <div className="mb-6">
        <DropZone onFiles={handleFiles} uploading={uploading} accept="image/*" compact />
        {currentFolder && <p className="text-xs text-stone-400 mt-1.5">Las subidas se guardarán en <strong className="text-amber-600">{currentFolder}</strong></p>}
      </div>

      {deleting && (
        <div className="bg-white rounded-lg border-2 border-red-300 p-6 mb-6" data-testid="media-delete-confirm">
          <h2 className="font-black text-stone-800 mb-2">Eliminar “{deleting.fileName}”</h2>
          <p className="text-sm text-stone-500 mb-4">Acción sensible: requiere PIN Maestro. Para retirarlo temporalmente usa el conmutador Activo.</p>
          <div className="flex gap-3">
            <button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 transition-colors text-white px-5 py-2.5 rounded text-sm font-bold" data-testid="media-delete-confirm-button">Eliminar</button>
            <button onClick={() => setDeleting(null)} className="px-4 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {selected.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-4 text-sm" data-testid="selection-bar">
              <CheckCircle2 className="w-4 h-4 text-amber-500 shrink-0" />
              <span className="font-bold text-stone-700 flex-1">{selected.length} {selected.length === 1 ? 'imagen seleccionada' : 'imágenes seleccionadas'} · arrástralas a una carpeta para moverlas</span>
              <button onClick={() => setSelected([])} className="text-xs font-bold text-stone-500 hover:text-stone-800 transition-colors" data-testid="clear-selection-button">Deseleccionar todo</button>
            </div>
          )}
          {view === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="media-grid">
              {!q && subFolders.map((f) => (
                <div
                  key={f.id}
                  onClick={() => setCurrentFolder(f.path)}
                  {...dropProps(f.path)}
                  {...folderDragProps(f)}
                  className={`cursor-pointer border rounded-lg transition-colors flex flex-col items-center justify-center py-6 relative group ${dragOver === f.path ? 'bg-amber-100 border-amber-400 ring-2 ring-amber-400' : 'border-stone-200 bg-amber-50/60 hover:bg-amber-50 hover:border-amber-300'}`}
                  data-testid={`folder-card-${f.id}`}
                >
                  <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity"><FolderMenu f={f} /></div>
                  <Folder className="w-10 h-10 text-amber-400 mb-2" fill="currentColor" />
                  <p className="text-xs font-bold text-stone-700 truncate px-3 max-w-full">{f.path.split('/').pop()}</p>
                </div>
              ))}
              {files.map((f) => (
                <button
                  key={f.id}
                  onClick={() => onFileClick(f)}
                  {...dragProps(f)}
                  className={`relative text-left border rounded-lg overflow-hidden transition-shadow bg-white hover:shadow-md cursor-grab active:cursor-grabbing ${selected.includes(f.id) ? 'ring-2 ring-amber-500 border-amber-400' : detail?.id === f.id ? 'ring-2 ring-amber-300 border-amber-200' : 'border-stone-200'} ${!f.active ? 'opacity-50' : ''}`}
                  data-testid={`media-card-${f.id}`}
                >
                  {selected.includes(f.id) && (
                    <span className="absolute top-1.5 left-1.5 bg-amber-500 text-white rounded-full p-0.5 z-10"><Check className="w-3.5 h-3.5" /></span>
                  )}
                  <img src={api.mediaThumb(f.id, 300)} alt={f.fileName} className="w-full h-28 object-cover bg-stone-100" loading="lazy" />
                  <div className="px-3 py-2">
                    <p className="text-xs font-bold text-stone-700 truncate">{f.fileName}</p>
                    <p className="text-[10px] text-stone-400">{formatSize(f.size)} · {new Date(f.uploadedAt).toLocaleDateString('es-ES')}{q && f.folder ? ` · 📁 ${f.folder}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-stone-200 rounded-lg divide-y divide-stone-100" data-testid="media-list">
              {!q && subFolders.map((f) => (
                <div key={f.id} onClick={() => setCurrentFolder(f.path)} {...dropProps(f.path)} {...folderDragProps(f)} className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${dragOver === f.path ? 'bg-amber-100 ring-2 ring-inset ring-amber-400' : 'hover:bg-amber-50/50'}`} data-testid={`folder-row-${f.id}`}>
                  <Folder className="w-5 h-5 text-amber-400 shrink-0" fill="currentColor" />
                  <span className="text-sm font-bold text-stone-700 flex-1 truncate">{f.path.split('/').pop()}</span>
                  <span className="text-xs text-stone-400">Carpeta</span>
                  <FolderMenu f={f} />
                </div>
              ))}
              {files.map((f) => (
                <div key={f.id} onClick={() => onFileClick(f)} {...dragProps(f)} className={`flex items-center gap-3 px-4 py-2 cursor-grab active:cursor-grabbing hover:bg-stone-50 transition-colors ${selected.includes(f.id) ? 'bg-amber-50' : detail?.id === f.id ? 'bg-amber-50/50' : ''} ${!f.active ? 'opacity-50' : ''}`} data-testid={`media-row-${f.id}`}>
                  {selected.includes(f.id)
                    ? <span className="bg-amber-500 text-white rounded-full p-0.5 shrink-0"><Check className="w-3 h-3" /></span>
                    : <span className="w-4 shrink-0" />}
                  <img src={api.mediaThumb(f.id, 100)} alt={f.fileName} className="w-9 h-9 object-cover rounded bg-stone-100 shrink-0" loading="lazy" />
                  <span className="text-sm font-bold text-stone-700 flex-1 truncate">{f.fileName}</span>
                  {q && f.folder && <span className="text-xs text-amber-600 font-bold truncate max-w-32">📁 {f.folder}</span>}
                  <span className="text-xs text-stone-400 w-20 text-right">{formatSize(f.size)}</span>
                  <span className="text-xs text-stone-400 w-20 text-right">{new Date(f.uploadedAt).toLocaleDateString('es-ES')}</span>
                </div>
              ))}
            </div>
          )}
          {files.length === 0 && subFolders.length === 0 && (
            <div className="text-center py-16 text-stone-400 bg-white border border-stone-200 rounded-lg mt-4">
              <FolderOpen className="w-10 h-10 mx-auto mb-3 text-stone-300" />
              <p className="text-sm">{q ? 'Sin resultados para tu búsqueda.' : 'Esta carpeta está vacía. Arrastra archivos para subirlos aquí.'}</p>
            </div>
          )}
        </div>

        {/* Detalle + trazabilidad */}
        <div>
          {detail ? (
            <div className="bg-white border border-stone-200 rounded-lg p-5 sticky top-6" data-testid="media-detail">
              <div className="flex items-start justify-between mb-3">
                <h2 className="font-black text-stone-800 text-sm break-all pr-2">{detail.fileName}</h2>
                <button onClick={() => setDetail(null)} className="text-stone-400 hover:text-stone-700 transition-colors shrink-0"><X className="w-4 h-4" /></button>
              </div>
              <img src={api.mediaThumb(detail.id, 600)} alt={detail.fileName} className="w-full h-40 object-cover rounded mb-4 bg-stone-100" />
              <div className="space-y-1.5 text-xs text-stone-500 mb-4">
                <p><strong className="text-stone-700">Subido:</strong> {new Date(detail.uploadedAt).toLocaleString('es-ES')}</p>
                <p><strong className="text-stone-700">Usuario:</strong> {detail.user}</p>
                <p><strong className="text-stone-700">Franquicia:</strong> {franchises.find((f) => f.id === detail.ownerId)?.name || detail.ownerId}</p>
                <p><strong className="text-stone-700">Tipo:</strong> {detail.contentType} · {formatSize(detail.size)}</p>
                <p><strong className="text-stone-700">Almacenamiento:</strong> {detail.provider === 'onedrive' ? 'OneDrive (Graph)' : 'Local (pendiente Graph)'}</p>
              </div>

              <div className="border-t border-stone-100 pt-3 mb-4">
                <p className="text-xs font-bold text-stone-700 mb-1.5 flex items-center gap-1.5"><Folder className="w-3.5 h-3.5" /> Carpeta</p>
                <select
                  value={detail.folder || ''}
                  onChange={(e) => moveFile(detail, e.target.value)}
                  className="w-full border border-stone-300 rounded px-2.5 py-2 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-400"
                  data-testid="media-detail-folder"
                >
                  <option value="">Sin carpeta (raíz)</option>
                  {folders.map((f) => <option key={f.id} value={f.path}>{f.path}</option>)}
                </select>
              </div>

              <div className="border-t border-stone-100 pt-3 mb-4">
                <p className="text-xs font-bold text-stone-700 mb-2 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Dónde se utiliza</p>
                {usage.length === 0 ? (
                  <p className="text-xs text-stone-400">No se utiliza en ningún módulo todavía.</p>
                ) : (
                  <ul className="space-y-1" data-testid="media-usage-list">
                    {usage.map((u, i) => (
                      <li key={i} className="text-xs text-teal-700 flex items-center gap-1.5">✓ {u.module} · {u.name}</li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-stone-100 pt-3">
                <div className="flex items-center gap-2">
                  <Switch checked={detail.active} onCheckedChange={() => toggleActive(detail)} data-testid="media-detail-active" />
                  <span className="text-xs text-stone-500">{detail.active ? 'Activo' : 'Inactivo'}</span>
                </div>
                <button onClick={() => setDeleting(detail)} className="flex items-center gap-1.5 text-xs font-bold text-red-500 hover:text-red-600 transition-colors" data-testid="media-detail-delete">
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-stone-50 border border-dashed border-stone-300 rounded-lg p-8 text-center text-stone-400 text-sm">
              Selecciona un archivo para ver sus detalles y dónde se utiliza.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
